import type { DocumentNode } from '@apollo/client';
import { type CardProps, type CardTabListType } from 'antd/es/card';
import { type ColumnGroupType, type ColumnType, type TableProps } from 'antd/es/table';
import { isEmpty, isEqual } from 'lodash';
import { type TabBarExtraContent } from 'rc-tabs/lib/interface';
import { createContext, useEffect, useMemo, useState } from 'react';

import type { DefectPatternsScope } from 'horizon/components/Damages/DefectPatterns';
import { type AtlasGqlTableView } from 'types/atlas-graphql';

import { DataTable } from './DataTable';
import {
  type EFilterSortClearType,
  type ESortOptions,
  type TDataTableContext,
  type TPersistedState,
  type TQueryFilter,
} from './types';
import { useDefaultState } from './useDefaultState';
import { genericClientTableStorageIds, tableStorage } from './utils';
import { removeDefaultFilters } from './Views/utils';

// `any` records: DataTable never knows its row type (server tables get rows from `dataQuery`), and
// antd 5's ColumnType<unknown> rejects columns typed as TTableColumnDef<R> where they're defined.
type ColumnsType = ((ColumnGroupType<any> | ColumnType<any>) & {
  additionalFilterFunctions?: {
    [key: string]: (value: string | any[], record: any) => boolean;
  };
})[];

export type PresetFilterType = { value?: { sortOrder?: { columnKey: string; order: string } } } & {
  value?: { filters?: { [x: string]: any } };
} & { key?: string };

export interface DataTableProps extends Omit<TableProps<any>, 'title' | 'columns'> {
  actions?: JSX.Element;
  additionalDropdownActions?: any[];
  additionalDropdownViews?: any[];
  cardType?: CardProps['type'];
  columns?: ColumnsType;
  /**
   * True while `columns` are still built from async data (keys or filter options);
   * `?filter.<columnKey>=` URL params are applied once it is false.
   */
  columnsLoading?: boolean;
  bannerMessage?: string;
  /**
   * When true and the table has no active column filters, skip the server query
   * and show the large-record filter banner until a filter is applied.
   */
  deferQueryUntilFiltered?: boolean;
  controls?: JSX.Element;
  dataQuery?: DocumentNode;
  setLoading?: (loading: boolean) => void;
  setTotalCount?: (totalCount: number) => void;
  data?: TableProps<Record<string, any>>['dataSource'];
  clearSortAndFilterType?: EFilterSortClearType;
  disableUserSort?: boolean;
  fillWidth?: boolean;
  hasServerDataTableContextProviderParent?: boolean; // pass as true when ServerDataTableContext is already provided in the component tree
  id?: string;
  initialPresetFilter?: PresetFilterType;
  initialSort?: { key: string; sort: ESortOptions };
  onClick?: (record: any) => void;
  // overrides DataTable's own handler; `undefined` removes Clear Filter & Sort.
  onClearSortAndFilter?: () => void;
  onDataQueryChange?: (args: any) => void;
  onSavePriority?: (prioritizedItems: any[]) => void;
  onTabChange?: (key: string) => void;
  persistTableState?: boolean;
  renderInContext?: React.ReactNode; // render the node within the `DataTableContext.Provider` context
  renderInServerContext?: React.ReactNode; // render the node within the `ServerDataTableContext.Provider` context
  routeProps?: any;
  /** Sanitizes table-local persisted state before it initializes context. */
  sanitizePersistedState?: (
    state: TPersistedState | null | undefined
  ) => TPersistedState | null | undefined;
  deferCount?: boolean;
  setShouldShowMultisort?: React.Dispatch<React.SetStateAction<boolean>>;
  setDisableTopLevelFilters?: React.Dispatch<React.SetStateAction<boolean>>;
  scrollWidth?: number;
  scrollHeight?: number;
  showSavedViews?: boolean;
  showColumnPicker?: boolean;
  showMultiSorter?: boolean;
  // Shows the Defect Patterns menu, scoped to these Sites or Assets
  defectPatternsScope?: DefectPatternsScope;
  // Filters the page sets that the user cannot change. Conditions lists them.
  lockedFilters?: TQueryFilter[];
  savedViewCustomFields?: any;
  onSavedViewChange?: (tableView: AtlasGqlTableView) => void;
  storageKey?: { COLUMN_PICKER: string; MULTI_SORT: string };
  tabList?: CardTabListType[];
  tabBarExtraContent?: TabBarExtraContent;
  tabBarPriorityContent?: TabBarExtraContent;
  title?: React.ReactNode;
  transform?: (data: any) => { items?: any[]; totalCount?: number };
  isChangePriorityVisible?: boolean;
  // Input type has to be any, because the fields depend on the columns defined for the table
  onFiltersChange?: (filters: any) => void;
  onClearSelection?: () => void;
  isExpandAllVisible?: boolean;
  handleExpandAll?: () => void;
  isExpandAllEnabled?: boolean;
  allExpanded?: boolean;
  // If set, this will disable the changePriority button and display the text in a tooltip around the disabled button
  changePriorityDisabledText?: string;
  onCurrentDataChange?: (data: any[]) => void;
  /** Full column definitions including hidden columns, used for server/client filter resolution. */
  filterColumns?: DataTableProps['columns'];
  // Recalculates the table scroll height when pagination changes for sticky header layout.
  onPaginationChange?: () => void;
}

const NOOP = () => {};
const GENERIC_HANDLER = () => {};

export const DataTableContext = createContext<TDataTableContext>({
  initialRouteVariables: {},
  initialPagination: {},
  pagination: { pageSize: 10, current: 1, countLoading: false },
  updatePagination: GENERIC_HANDLER,
  filteredInfo: undefined,
  updateFilteredInfo: GENERIC_HANDLER,
  sortedInfo: undefined,
  updateSortedInfo: GENERIC_HANDLER,
  customizedColumnSet: undefined,
  updateCustomizedColumnSet: GENERIC_HANDLER,
  multiSortDef: undefined,
  updateMultiSortDef: GENERIC_HANDLER,
  tableView: undefined,
  updateTableView: GENERIC_HANDLER,
  setPersistedState: GENERIC_HANDLER,
});

export const DataTableContextProvider: React.FunctionComponent<DataTableProps> = props => {
  const { defaultPagination, defaultFilteredInfo, defaultSortOrder, defaultRouteVariables } =
    useDefaultState(props);
  const { id: tableId } = props;

  const {
    get: getPersistedState = NOOP,
    set: setPersistedState = NOOP,
  }: {
    get: typeof NOOP | (() => TPersistedState);
    set: typeof NOOP | ((value: TPersistedState) => void);
  } = tableStorage({ storageKey: tableId });

  // FIXME: Certain tables include different sub-records depending on the active parent record.
  // We need more sophisticated storage management to persist pagination correctly for each parent.
  // For now we can correctly persist initial pagination for generic client tables in the list. - AN 8/20/24
  const shouldPersistInitialPagination = tableId
    ? genericClientTableStorageIds.includes(tableId)
    : undefined;

  const storedState = getPersistedState() as TPersistedState | null | undefined;
  const persistedState = props.sanitizePersistedState
    ? props.sanitizePersistedState(storedState)
    : storedState;

  const [tableView, setTableView] = useState<any | undefined>();
  const [customizedColumnSet, setCustomizedColumnSet] = useState<unknown[] | undefined>();
  const [multiSortDef, setMultiSortDef] = useState<unknown[] | undefined>();

  const [filteredInfo, setFilteredInfo] = useState(
    removeDefaultFilters(tableId, persistedState?.filteredInfo ?? defaultFilteredInfo) ??
      defaultFilteredInfo
  );
  const [sortedInfo, setSortedInfo] = useState(persistedState?.sortedInfo ?? defaultSortOrder);
  const [initialPagination] = useState(
    shouldPersistInitialPagination && persistedState?.pagination
      ? persistedState.pagination
      : defaultPagination
  );
  const [pagination, setPagination] = useState({
    // since persisted state (which comes from localStorage) cannot persist
    // functions and pagination options may contain functions (e.g. showTotal),
    // we need to spread the default options and let persisted options override
    // where necessary
    ...defaultPagination,
    ...persistedState?.pagination,
  });
  // FIXME ARS - really hope this can go away after the AssetChooser is included in the DataTabe
  const [initialRouteVariables] = useState(defaultRouteVariables);

  // Some values in pagination get updated async, specifically total, which we only know once we've received the query response.
  // So we need another callback to make sure we save the most recent version of everything to state.
  useEffect(() => {
    setPersistedState({ filteredInfo, sortedInfo, pagination });
  }, [pagination]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (persistedState?.filteredInfo && props?.onFiltersChange) {
      props.onFiltersChange(persistedState.filteredInfo);
    }
    // We only care about persistedState and props' initial values,
    // but we need to re-run this if the data changes in case onFilters change expects to operate on the most current data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  const contextValue = useMemo(
    () => ({
      initialRouteVariables,
      initialPagination,
      pagination,
      updatePagination: (newPagination: any) => {
        if (isEmpty(newPagination)) {
          setPagination(false);
        } else {
          setPagination((prevState: any) => {
            const newState = { ...prevState, ...newPagination };
            if (isEqual(prevState, newState)) {
              return prevState;
            }
            return newState;
          });
        }
      },
      filteredInfo,
      updateFilteredInfo: setFilteredInfo,
      sortedInfo,
      updateSortedInfo: setSortedInfo,
      customizedColumnSet,
      updateCustomizedColumnSet: setCustomizedColumnSet,
      multiSortDef,
      updateMultiSortDef: setMultiSortDef,
      tableView,
      updateTableView: setTableView,
      setPersistedState: ({
        filteredInfo,
        ...rest
      }: {
        filteredInfo: AtlasGqlTableView['filters'];
      }) => {
        setPersistedState({ filteredInfo: removeDefaultFilters(tableId, filteredInfo), ...rest });
      },
    }),
    [customizedColumnSet, tableView, multiSortDef, filteredInfo, sortedInfo, pagination]
  );

  return (
    <DataTableContext.Provider value={contextValue}>
      {props.children}
      {props.renderInContext}
    </DataTableContext.Provider>
  );
};

export default function DataTableWithContext(props: DataTableProps) {
  return (
    <DataTableContextProvider {...props}>
      <DataTable {...props} />
      {props.renderInContext}
    </DataTableContextProvider>
  );
}

export { DataTable as DataTableWithoutContext };
